/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId, IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { searchMediaLocationsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { MediaLocation, MediaUsageType } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { generateCacheKey, getSelectedProductIdFromActionInput } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Product Input.
 */
export class SearchMediaLocationsByIdInput implements IActionInput {
    public productId?: number;

    public channelId: number;

    public catalogId: number;

    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings, productId?: number, catalogId?: number) {
        this.productId = productId;
        this.channelId = +apiSettings.channelId;
        this.catalogId = catalogId || 0;
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'Key', value: 'SearchMediaLocationID' },
            { key: 'ProductId', value: this.productId },
            { key: 'CatalogId', value: this.catalogId }
        );

    public getCacheObjectType = () => 'MediaLocations';

    public dataCacheType = (): CacheType => 'none';
}

export const createSearchMediaLocationsByIdInput = (inputData: ICreateActionContext<IGeneric<IAny>>): SearchMediaLocationsByIdInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    const catalogId = getCatalogId(inputData.requestContext);
    if (!productId) {
        throw new Error('Unable to create SearchMediaLocationsByIdInput, no productId found on query');
    }
    return new SearchMediaLocationsByIdInput(inputData.requestContext.apiSettings, parseInt(productId, 10), catalogId);
};

export async function searchMediaLocationsByIdAction(input: SearchMediaLocationsByIdInput, ctx: IActionContext): Promise<MediaLocation[]> {
    // Only supported by Omni Channel, so if the feature is not enabled, return an empty array
    if (input.productId && ctx.requestContext.app.config.OmniChannelMedia) {
        const results = await searchMediaLocationsAsync({ callerContext: ctx }, {
            CatalogId: input.catalogId,
            ChannelId: input.channelId,
            ProductId: input.productId,
            MediaUsageTypeValue: MediaUsageType.All
        });
        return results || [];
    }
    return [];
}

export const searchMediaLocationsByIdActionDataAction = createObservableDataAction({
    id: 'search-media-locations-by-id',
    action: <IAction<MediaLocation[] | null>>searchMediaLocationsByIdAction,
    input: createSearchMediaLocationsByIdInput
});

export default searchMediaLocationsByIdActionDataAction;
